// Metronom offline (Faza 4?): jedyny modul dotykajacy Web Audio API poza
// `mikrofon.ts`. W odroznieniu od `mikrofon.ts` NIE dotyka `getUserMedia` -
// to jest ZRODLO dzwieku (OscillatorNode), nie sluchacz. Zero plikow audio:
// klik jest syntezowany, wiec dziala offline (PWA bez sieci) bez zadnego
// zasobu do pobrania.
//
// Wzorzec schedulera to "lookahead scheduling" (standardowa technika Web
// Audio API dla dokladnego tempa): `setTimeout` sam w sobie jest za
// niedokladny (throttling karty w tle, jitter kilkunastu ms), wiec NIE
// planuje dzwieku bezposrednio w momencie odpalenia timera. Zamiast tego co
// `LOOKAHEAD_MS` sprawdza zegar `AudioContext.currentTime` (audio-hardware,
// dokladny) i doplanowuje wszystkie uderzenia mieszczace sie w oknie
// `SCHEDULE_AHEAD_S` naprzod - sam dzwiek gra o czasie zaplanowanym w
// `OscillatorNode.start(time)`, ktory silnik audio wykona dokladnie, nawet
// jesli JS w tej chwili jest zajety czyms innym.

export interface MetronomOpcje {
  bpm: number;
  /** Liczba uderzen w takcie. Domyslnie 4, gdy pominiete. */
  beatsPerMeasure?: number;
  /** Wolane przy KAZDYM zaplanowanym uderzeniu, z numerem uderzenia w takcie
   * (0 = pierwsza, akcentowana). Wolane W PRZYBLIZENIU o czasie, w ktorym
   * uderzenie faktycznie brzmi - nie natychmiast przy zaplanowaniu, ktore
   * dzieje sie do `SCHEDULE_AHEAD_S` wczesniej. */
  onBeat?: (beat: number) => void;
}

export type StopFn = () => void;

const DOMYSLNE_BEATS_PER_MEASURE = 4;

/** Co ile ms scheduler sprawdza zegar audio i doplanowuje kolejne uderzenia. */
const LOOKAHEAD_MS = 25;
/** Jak daleko naprzod (w sekundach zegara audio) scheduler ma prawo
 * zaplanowac uderzenia w JEDNYM przebiegu `tick()`. Wieksze od `LOOKAHEAD_MS`
 * z zapasem, zeby jeden spozniony `tick()` (karta w tle) nie zostawil dziury
 * ciszy zanim dogoni. */
const SCHEDULE_AHEAD_S = 0.1;

const CZESTOTLIWOSC_AKCENT_HZ = 880;
const CZESTOTLIWOSC_ZWYKLY_HZ = 440;
const CZAS_TRWANIA_AKCENT_S = 0.05;
const CZAS_TRWANIA_ZWYKLY_S = 0.03;

/** Gra jeden klik: sinusoida z krotka obwiednia (gain w dol do prawie zera),
 * zeby dzwiek nie ucinal sie "klik-trzask" na koncu. `stop()` z zapasem 10ms
 * po koncu obwiedni, zeby ogon rampy zdazyl dograc. */
function zagrajKlik(kontekst: AudioContext, czas: number, czestotliwosc: number, czasTrwania: number): void {
  const oscylator = kontekst.createOscillator();
  const wzmocnienie = kontekst.createGain();
  oscylator.type = 'sine';
  oscylator.frequency.value = czestotliwosc;
  oscylator.connect(wzmocnienie);
  wzmocnienie.connect(kontekst.destination);

  wzmocnienie.gain.setValueAtTime(1, czas);
  // `exponentialRampToValueAtTime` nie akceptuje 0 (dzielenie logarytmiczne),
  // stad prawie-zero zamiast ciszy.
  wzmocnienie.gain.exponentialRampToValueAtTime(0.0001, czas + czasTrwania);

  oscylator.start(czas);
  oscylator.stop(czas + czasTrwania + 0.01);
}

/** Wywoluje `onBeat(beat)` w chwili jak najblizszej faktycznemu brzmieniu
 * uderzenia (`czasWAudio`, w sekundach zegara `kontekst.currentTime`), nie w
 * chwili zaplanowania - `setTimeout` z wyliczonym opoznieniem jest tu
 * wystarczajaco dokladny, bo UI (podswietlenie, wskaznik) nie musi trzymac
 * dokladnosci audio, tylko byc "w okolicy" tego, co slychac. */
function zaplanujCallback(kontekst: AudioContext, czasWAudio: number, beat: number, onBeat: (beat: number) => void): void {
  const opoznienieMs = Math.max(0, (czasWAudio - kontekst.currentTime) * 1000);
  setTimeout(() => onBeat(beat), opoznienieMs);
}

/**
 * Uruchamia ciagly metronom (tyka az do wywolania zwroconej funkcji `stop`).
 * Tworzy WLASNY `AudioContext`, zamykany w `stop()` - dwa niezalezne
 * wywolania `startMetronom` (np. przy pomylce wywolujacego) tworza dwa
 * niezalezne konteksty, kazdy sprzatany swoim `stop()`.
 */
export function startMetronom(opcje: MetronomOpcje): StopFn {
  const { bpm, onBeat } = opcje;
  const beatsPerMeasure = opcje.beatsPerMeasure ?? DOMYSLNE_BEATS_PER_MEASURE;
  const sekundNaUderzenie = 60 / bpm;

  const kontekst = new AudioContext();
  let nastepnyCzas = kontekst.currentTime;
  let nastepneUderzenie = 0;
  let zatrzymany = false;

  function tick(): void {
    if (zatrzymany) return;
    while (nastepnyCzas < kontekst.currentTime + SCHEDULE_AHEAD_S) {
      const beat = nastepneUderzenie % beatsPerMeasure;
      const czestotliwosc = beat === 0 ? CZESTOTLIWOSC_AKCENT_HZ : CZESTOTLIWOSC_ZWYKLY_HZ;
      const czasTrwania = beat === 0 ? CZAS_TRWANIA_AKCENT_S : CZAS_TRWANIA_ZWYKLY_S;
      zagrajKlik(kontekst, nastepnyCzas, czestotliwosc, czasTrwania);
      if (onBeat) zaplanujCallback(kontekst, nastepnyCzas, beat, onBeat);
      nastepnyCzas += sekundNaUderzenie;
      nastepneUderzenie += 1;
    }
  }

  tick();
  const idInterwalu = setInterval(tick, LOOKAHEAD_MS);

  return function stop(): void {
    if (zatrzymany) return;
    zatrzymany = true;
    clearInterval(idInterwalu);
    if (kontekst.state !== 'closed') void kontekst.close();
  };
}

/**
 * Odlicza DOKLADNIE `beatsPerMeasure` klikniec (jeden takt) i rozwiazuje
 * obietnice PO ostatnim z nich - wywolujacy (przycisk "odlicz" w panelu
 * metronomu, `main.ts`) czeka na te obietnice, zeby np. wystartowac
 * nagrywanie albo wlasciwy metronom dokladnie na "raz" kolejnego taktu.
 * W odroznieniu od `startMetronom` NIE zwraca `StopFn` - odliczenie jednego
 * taktu jest krotkie i nieprzerywalne z zalozenia (przerwanie w polowie
 * odliczenia zostawiloby gitarzyste bez informacji, na ktorym jest uderzeniu).
 */
export function odlicz(opcje: MetronomOpcje): Promise<void> {
  const { bpm, onBeat } = opcje;
  const beatsPerMeasure = opcje.beatsPerMeasure ?? DOMYSLNE_BEATS_PER_MEASURE;
  const sekundNaUderzenie = 60 / bpm;

  return new Promise((resolve) => {
    const kontekst = new AudioContext();
    // Maly zapas startowy (a nie `kontekst.currentTime` wprost), zeby
    // pierwszy klik mial szanse zostac zaplanowany PRZED momentem, w ktorym
    // silnik audio go odtwarza - zaplanowanie w przeszlosci bywa cicho
    // ignorowane przez niektore implementacje.
    const czasStartu = kontekst.currentTime + 0.05;

    for (let beat = 0; beat < beatsPerMeasure; beat += 1) {
      const czas = czasStartu + beat * sekundNaUderzenie;
      const czestotliwosc = beat === 0 ? CZESTOTLIWOSC_AKCENT_HZ : CZESTOTLIWOSC_ZWYKLY_HZ;
      const czasTrwania = beat === 0 ? CZAS_TRWANIA_AKCENT_S : CZAS_TRWANIA_ZWYKLY_S;
      zagrajKlik(kontekst, czas, czestotliwosc, czasTrwania);
      if (onBeat) zaplanujCallback(kontekst, czas, beat, onBeat);
    }

    const czasKonca = czasStartu + beatsPerMeasure * sekundNaUderzenie;
    const opoznienieMs = Math.max(0, (czasKonca - kontekst.currentTime) * 1000);
    setTimeout(() => {
      if (kontekst.state !== 'closed') void kontekst.close();
      resolve();
    }, opoznienieMs);
  });
}
